const React = require('react');
const { createRoot } = require('react-dom/client');
const { wrapIslandTree } = require('public/js/islands/wrap-island-tree.js');
const { MEDIA_TYPE_3D_MODEL } = require('public/templates/modules/product_media/utils/model-3d.js');
const { trackProductMedia3dButtonClicked } = require('public/templates/modules/product_media/utils/product-media-3d-launch-analytics.js');
const { HERO_3D_MODEL_MIN_HEIGHT_PX } = require('public/templates/modules/product_media/utils/product-media-3d-viewer-mount.js');
const GalleryThumb3D = require('public/templates/modules/product_media/components/GalleryThumb3D.jsx').default;
const ProductMedia3dModelViewer = require('public/templates/modules/product_media/components/ProductMedia3dModelViewer.jsx').default;
const {
	PRODUCT_IMAGES_3D_MODEL_CLASS_NAMES,
	PRODUCT_IMAGES_3D_MODEL_ERROR_CLASS_NAMES,
} = require('public/templates/modules/product_media/utils/product-media-3d-viewer.js');
const MEDIA_TYPE_3D_MODEL_VALUE = MEDIA_TYPE_3D_MODEL;
const PRODUCT_MAIN_IMAGE_HERO_HIDDEN_CLASS = 'product-main-image-link--hero-hidden';

/** @type {WeakMap<HTMLElement, import('react-dom/client').Root>} */
const viewerRoots = new WeakMap();

/** @type {WeakMap<HTMLElement, import('react-dom/client').Root>} */
const thumbRoots = new WeakMap();

/**
 * @param {ParentNode} container
 * @returns {string}
 */
function readUnableToLoadModelMessage(container) {
	const moduleElement = container.closest('.product-images-module');
	const message = moduleElement?.dataset.modelViewerUnableToLoad;

	return typeof message === 'string' && message !== ''
		? message
		: 'Error: Unable To Load 3D Model';
}

/**
 * @param {HTMLElement} viewerContainer
 */
function destroyProductImages3dViewer(viewerContainer) {
	const root = viewerRoots.get(viewerContainer);
	if (root !== undefined) {
		root.unmount();
		viewerRoots.delete(viewerContainer);
	}

	delete viewerContainer.dataset.reactIslandRoot;
	viewerContainer.classList.remove('product-3d-model__viewer--error');
	viewerContainer.replaceChildren();
}

/**
 * @param {HTMLElement} viewerContainer
 * @param {string} modelUrl
 */
function mountProductImages3dViewer(viewerContainer, modelUrl) {
	destroyProductImages3dViewer(viewerContainer);

	viewerContainer.dataset.reactIslandRoot = 'true';

	const labels = {
		unableToLoadModel: readUnableToLoadModelMessage(viewerContainer),
	};
	const root = createRoot(viewerContainer);
	viewerRoots.set(viewerContainer, root);

	const element = wrapIslandTree(
		React.createElement,
		ProductMedia3dModelViewer,
		{
			classNames: PRODUCT_IMAGES_3D_MODEL_CLASS_NAMES,
			errorClassNames: PRODUCT_IMAGES_3D_MODEL_ERROR_CLASS_NAMES,
			labels,
			modelUrl,
		},
		viewerContainer,
	);
	root.render(element);
}

/**
 * @param {import('jquery')} $module
 */
function hideProductImages3dViewer($module) {
	$module.find('.product-3d-model').hide();
	$module.find('.main-image').removeClass('main-image--showing-3d');
	$module.find('.product-main-image-link').removeClass(PRODUCT_MAIN_IMAGE_HERO_HIDDEN_CLASS).show();
	$module.find('.product-3d-model__viewer').each((_index, viewerContainer) => {
		destroyProductImages3dViewer(viewerContainer);
	});
}

/**
 * @param {HTMLElement} thumbContainer
 */
function unmountProductImages3dThumb(thumbContainer) {
	const root = thumbRoots.get(thumbContainer);
	if (root !== undefined) {
		root.unmount();
		thumbRoots.delete(thumbContainer);
	}

	delete thumbContainer.dataset.reactIslandRoot;
}

/**
 * @param {import('jquery')} $module
 */
function unmountProductImages3dThumbs($module) {
	$module.find('.product-secondary-image--3d .product-secondary-image__thumb-3d').each((_index, thumbContainer) => {
		unmountProductImages3dThumb(thumbContainer);
	});
}

/**
 * @param {import('jquery')} $module
 */
function cleanupProductImages3dModule($module) {
	hideProductImages3dViewer($module);
	unmountProductImages3dThumbs($module);
}

/**
 * @param {HTMLElement} thumbContainer
 */
function mountProductImages3dThumb(thumbContainer) {
	const existingRoot = thumbRoots.get(thumbContainer);
	if (existingRoot !== undefined) {
		return;
	}

	thumbContainer.dataset.reactIslandRoot = 'true';
	const root = createRoot(thumbContainer);
	thumbRoots.set(thumbContainer, root);

	const element = wrapIslandTree(
		React.createElement,
		GalleryThumb3D,
		{},
		thumbContainer,
	);
	root.render(element);
}

/**
 * @param {import('jquery')} $module
 */
function mountProductImages3dThumbs($module) {
	$module.find('.product-secondary-image--3d .product-secondary-image__thumb-3d').each((_index, thumbContainer) => {
		mountProductImages3dThumb(thumbContainer);
	});
}

/**
 * @param {import('jquery')} $module
 * @param {string} modelUrl
 * @param {{
 *   catalog?: string,
 *   skuStripped?: string,
 *   skipTracking?: boolean,
 * }} [trackingDetail]
 */
function showProductImages3dViewer($module, modelUrl, trackingDetail = {}) {
	const normalizedModelUrl = modelUrl.trim();
	if (normalizedModelUrl === '') {
		return;
	}

	if (trackingDetail.skipTracking !== true) {
		trackProductMedia3dButtonClicked({
			catalog: trackingDetail.catalog,
			mediaType: MEDIA_TYPE_3D_MODEL_VALUE,
			skuStripped: trackingDetail.skuStripped,
		});
	}

	$module.find('.product-main-image-link').addClass(PRODUCT_MAIN_IMAGE_HERO_HIDDEN_CLASS).hide();
	$module.find('.main-image').addClass('main-image--showing-3d');
	$module.find('.product-video').hide();

	const $viewerRegion = $module.find('.product-3d-model');
	const viewerContainer = $viewerRegion.find('.product-3d-model__viewer').get(0);

	if (viewerContainer === undefined) {
		return;
	}

	$viewerRegion.show();
	mountProductImages3dViewer(viewerContainer, normalizedModelUrl);
}

module.exports = {
	HERO_3D_MODEL_MIN_HEIGHT_PX,
	MEDIA_TYPE_3D_MODEL_VALUE,
	cleanupProductImages3dModule,
	destroyProductImages3dViewer,
	hideProductImages3dViewer,
	mountProductImages3dThumbs,
	mountProductImages3dViewer,
	showProductImages3dViewer,
	unmountProductImages3dThumb,
	unmountProductImages3dThumbs,
};
